import { Grid, IconButton } from '@material-ui/core';
import api, { PresencaResponse } from 'api/Assessor/presenca';
import AssembleiaDateTime from 'components/AssembleiaDateTime';
import Breadcrumb from 'components/BreadCrumb/BreadCrumb';
import { IBreadCrumbItem } from 'components/BreadCrumb/BreadCrumbItem';
import { BackIcon } from 'components/Icons';
import LayoutContext from 'components/Layout/LayoutContext';
import Loading from 'components/Loading';
import PageTitle from 'components/PageTitle/PageTitle';
import Page from 'components/PrivateContainer';
import { ITopBarMenuItem } from 'components/TopBar/TopBarMenuItem';
import UnauthorizedScreen from 'components/UnauthorizedScreen/UnauthorizedScreen';
import { parseISO } from 'date-fns';
import { DateFormats } from 'enuns/DateTypes';
import { TopBarMenuAcessoRapido } from 'enuns/TopBarMenuAcessoRapido';
import { AssembleiaAssessorRoutePath, AssessorRoutePath, MainAssessorRoutePath } from 'library/Routes';
import PresencasResponse from 'models/PresencasResponse';
import React, { useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { assembleiasAssessorActionsAtualizarPresencas, assembleiasAssessorLoadEvento } from 'store/modules/AssembleiasAssessorActions';
import { IAssembleiasAssessorReducer } from 'store/modules/AssembleiasAssessorReducer';
import { IAuthReducer } from 'store/modules/AuthReducer';
import { IReducer } from 'store/Reducer';
import { history } from 'store/Store';
import { wsStartStop } from 'utils/AssembleiaUtils';
import AxiosErrorHandler from 'utils/AxiosErrorHandler';
import { getFormatedDateTime } from 'utils/DateUtils';
import DownloadTrigger from 'utils/DownloadTrigger';
import ErrorHandler from 'utils/ErrorHandler';
import { getAcessoRapido } from 'utils/TopBarMenuUtils';
import Context, { IRelatorioPresentesAssessorContext } from './Context';
import Filtros from './Filtros';
import { relatorioInitialState, relatorioReducer } from './reducer';
import useStyle from './style';
import Tabela from './Tabela';
import Totais from './Totais';

interface PageParams {
  idAssembleia: string;
}

export default function () {
  const { t } = useTranslation();
  const classes = useStyle();
  const { idAssembleia } = useParams<PageParams>();
  const { evento, loadingEvento, loadingListaEvento } = useSelector<IReducer, IAssembleiasAssessorReducer>(
    state => state.assembleiasAssessor
  );
  const { usuario } = useSelector<IReducer, IAuthReducer>(state => state.auth);
  const dispatchAction = useDispatch();
  const [state, dispatch] = useReducer(relatorioReducer, relatorioInitialState);
  const [gerandoRelatorio, setGeracao] = useState<boolean>(false);
  const [relatorio, setRelatorio] = useState<PresencaResponse>();
  const [cooperativas, setCooperativas] = useState<string[]>([]);
  const { opened: wsOpened } = useSelector((state: IReducer) => state.wsAssembleias);
  const location = useLocation();
  const [atualizandoPresencas, setAtualizarPresencas] = useState<boolean>(false);
  const [atualizacoesPresenca, setAtualizacoesPresenca] = useState<number>(0);
  const [presencas, setPresencas] = useState<PresencasResponse>();
  const { setPageMenuItems } = useContext(LayoutContext);
  const [initialized, setInitialized] = useState<boolean>();

  const breadcrumbItens = useMemo((): Array<IBreadCrumbItem> => {
    if (!evento) return [];
    return [
      {
        nome: t('MainRoute.title'),
        url: MainAssessorRoutePath,
      },
      {
        nome: evento.nome,
        url: AssembleiaAssessorRoutePath.replace(':id', `${evento.id}`),
      },
      {
        nome: t('RelatorioPresentes.title'),
      },
    ];
  }, [evento, t]);

  const backToEvent = useCallback(() => {
    if (!evento) return;
    history.push(AssembleiaAssessorRoutePath.replace(':id', `${evento.id}`));
  }, [evento]);

  const needLoadEvento = useMemo(() => {
    if (loadingListaEvento || loadingEvento) return false;
    if (!evento) return true;
    if (evento.id !== parseInt(idAssembleia)) return true;
    return false;
  }, [evento, idAssembleia, loadingEvento, loadingListaEvento]);

  const clearConfiguration = useMemo(() => {
    const clear = Object.assign({}, { ...state, idAssembleia: parseInt(idAssembleia) });
    if (!clear.coops?.length) {
      clear.coops = undefined;
    }
    if (!clear.cpfOrNome?.length) {
      clear.cpfOrNome = undefined;
    }
    if (!clear.data?.length) {
      clear.data = undefined;
    }
    return clear;
  }, [state, idAssembleia]);

  const gerarRelatorio = useCallback(async () => {
    setGeracao(true);
    const { fail, data } = await api.relatorio(clearConfiguration);
    setGeracao(false);
    if (fail) {
      ErrorHandler(fail);
    }
    if (data) {
      const fileDate = getFormatedDateTime(Date.now(), DateFormats.ISOFile);
      DownloadTrigger(
        `Relatorio_Presentes_${fileDate}_${idAssembleia}.xls`,
        data
      );
    }
  }, [clearConfiguration, setGeracao, idAssembleia]);

  const subMenu = useMemo<ITopBarMenuItem[]>(() => {
    if (usuario) {
      const menus: ITopBarMenuItem[] = getAcessoRapido(t, usuario.perfil, TopBarMenuAcessoRapido.Presentes, idAssembleia);

      if (typeof relatorio?.totalElements === 'number' && relatorio.totalElements > 0) {
        menus.push({
          label: t(`SubMenus.baixarRelatorio`),
          onClick: gerarRelatorio,
          disabled: loadingListaEvento || gerandoRelatorio
        });
      }

      return menus;
    }

    return [];

  }, [t, usuario, idAssembleia, loadingListaEvento, gerarRelatorio, gerandoRelatorio, relatorio])

  useEffect(() => {
    setPageMenuItems(subMenu);
    return () => setPageMenuItems();
  }, [subMenu, setPageMenuItems]);

  useEffect(() => {
    if (usuario) {
      wsStartStop(
        usuario,
        Number(idAssembleia),
        dispatchAction,
        wsOpened
      )
    }
  }, [usuario, location, idAssembleia, dispatchAction, wsOpened])

  const atualizarPresencas = useCallback(async () => {
    if (!evento?.id || evento.id !== parseInt(idAssembleia)) return;
    setAtualizacoesPresenca(atualizacoesPresenca + 1);
    setAtualizarPresencas(true);
    dispatchAction(
      assembleiasAssessorActionsAtualizarPresencas(
        evento.id,
        data => {
          setPresencas(data);
          setAtualizarPresencas(false);
        },
        fail => {
          ErrorHandler(fail);
          setAtualizarPresencas(false);
        }
      )
    );
  }, [dispatchAction, evento, setAtualizarPresencas, idAssembleia, setAtualizacoesPresenca, atualizacoesPresenca]);

  useEffect(() => {
    if (atualizacoesPresenca === 0 && !atualizandoPresencas) {
      atualizarPresencas();
    }
  }, [atualizarPresencas, atualizacoesPresenca, atualizandoPresencas])

  useEffect(() => {
    if (needLoadEvento && !initialized) {
      setInitialized(true);
      dispatchAction(
        assembleiasAssessorLoadEvento(
          parseInt(idAssembleia),
          () => {setInitialized(false)},
          error => {
            AxiosErrorHandler(error);
            if (error.response?.status === 417) {
              history.push(AssessorRoutePath);
            }
          }
        )
      );
    }
  }, [needLoadEvento, dispatchAction, idAssembleia, initialized, setInitialized]);

  useEffect(() => {
    const getRelatorio = async () => {
      const { fail, data } = await api.list({
        ...state,
        idAssembleia: parseInt(idAssembleia),
      });
      if (fail) {
        ErrorHandler(fail);
      }
      if (data) {
        setRelatorio(data);
      }
    };
    const getCooperativas = async () => {
      const { fail, data } = await api.cooperativas(parseInt(idAssembleia));
      if (fail) {
        ErrorHandler(fail);
      }
      if (data) {
        setCooperativas(data);
      }
    };
    if (!needLoadEvento) {
      getRelatorio();
      getCooperativas();
    }
  }, [needLoadEvento, idAssembleia, state]);

  const contextValues = useMemo<IRelatorioPresentesAssessorContext>((): IRelatorioPresentesAssessorContext => {
    return {
      presencas: presencas || null,
      atualizarPresencas,
      atualizandoPresencas,
    }
  }, [presencas, atualizarPresencas, atualizandoPresencas])

  if (
    !usuario?.perfil.assessorRoute.assembleias.ver ||
    !usuario?.perfil.assessorRoute.assembleias.participantes.verTotal
  ) {
    return <UnauthorizedScreen />;
  }

  if (loadingListaEvento || needLoadEvento || loadingEvento) {
    return (
      <Page>
        <Loading />
      </Page>
    );
  }

  return (
    <Context.Provider value={contextValues}>
      <Page
        headerClassName={classes.header}
        header={
          <React.Fragment>
            <div className={classes.headline}>
              <IconButton onClick={backToEvent} className={classes.backButton}>
                <BackIcon />
              </IconButton>
              <PageTitle title={t('RelatorioPresentes.title')} />
            </div>
            <div className={classes.info}>
              <Breadcrumb itens={breadcrumbItens} />
              <AssembleiaDateTime date={parseISO(evento!.dataEvento)} dateFim={parseISO(evento!.dataEventoFim)}/>
            </div>
          </React.Fragment>
        }
      >
        <Grid container spacing={3} justify="space-between">
          <Grid item xs={12}>
            <Totais
              className={classes.totais}
              presencas={presencas}
            />
          </Grid>
        </Grid>
        <Filtros
          listaCoops={cooperativas}
          onChangeFilter={value => {
            dispatch({ type: 'setFiltros', value });
          }}
        />
        {relatorio && (
          <Tabela
            relatorio={relatorio}
            page={state.page}
            size={state.size}
            onChangePage={page =>
              dispatch({
                type: 'setPaginacao',
                value: { page },
              })
            }
            onChangeSize={(size, page) =>
              dispatch({
                type: 'setPaginacao',
                value: { size, page },
              })
            }
          />
        )}
        {(loadingEvento || gerandoRelatorio) && <Loading />}
      </Page>
    </Context.Provider>
  );
}
